/**
 * WebSocket Orders Service
 *
 * Recebe os eventos de pedido do orders-service e mantém um conjunto de
 * "overlays" de status — o estado mais recente conhecido de cada pedido, que a
 * tela sobrepõe aos dados carregados por HTTP.
 *
 * Contexto das mudanças em relação à versão anterior:
 *
 *  - O contrato não batia. O servidor envia `{action:"update"|"created"|
 *    "deleted", ...}` em minúsculo e sem campo `type`; o cliente esperava
 *    `type` e `action` em MAIÚSCULO. Nenhum evento casava, tudo caía no
 *    tratamento de desconhecido e terminava chamando os assinantes com lista
 *    vazia — o que o hook interpretava como "novo pedido" e disparava um
 *    GET /orders/today completo. Na prática cada evento do WebSocket virava um
 *    refetch total em cada painel conectado.
 *
 *  - Agora `update` é aplicado localmente (sem HTTP). Só `created` e `deleted`
 *    pedem recarga, porque o evento cru não traz produtos nem valor.
 *
 *  - Convergência: recarga obrigatória a cada reconexão, descarte de overlays
 *    já incorporados pelo snapshot HTTP, e uma rede de segurança periódica
 *    conduzida pelo hook.
 */

import type { WebSocketConnectionStatus } from './orderTypes';
import { apiClient } from './apiClient';
import { wsLog } from '@utils/wsLogger';

const WEBSOCKET_CONFIG = {
  URL: import.meta.env.VITE_WS_URL || '',
  RECONNECT_DELAY: 1000,
  MAX_RECONNECT_DELAY: 30000,
  RECONNECT_BACKOFF_FACTOR: 1.5,
  CONNECTION_TIMEOUT: 10000,
} as const;

/** Ações emitidas pelo trigger do banco. */
type ServerAction = 'created' | 'update' | 'deleted';

/**
 * Vocabulário de status do orders-service.
 *
 * O overlay carrega o status CRU, e não o enum OrderStatus, porque ele é
 * sobreposto ao pedido vindo do GET /orders/today — que também traz o valor
 * cru. Traduzir aqui para o enum ('fila' -> 'pendente', 'preparando' ->
 * 'em_preparo') faria a tela não reconhecer o valor e cair no default 'fila':
 * um pedido em preparo apareceria na coluna errada.
 */
export type ServerOrderStatus = 'fila' | 'preparando' | 'pronto' | 'entregue' | 'cancelado';

/** Estado mais recente conhecido de um pedido, vindo do stream. */
export interface OrderStatusOverlay {
  id: string;
  status: ServerOrderStatus;
  order_number?: string;
  student_name?: string;
  student_class?: string;
  /** Momento (relógio local) em que o evento chegou. Usado na reconciliação. */
  receivedAt: number;
}

export interface WebSocketServiceConfig {
  establishmentId: string;
  intervalId?: string;
  payload?: 'raw' | 'full';
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: WebSocketConnectionStatus) => void;
  /** Overlays mudaram: a tela pode reaplicar sem ir ao servidor. */
  onOverlays?: (overlays: OrderStatusOverlay[]) => void;
  /** Precisa recarregar via HTTP (pedido novo, remoção, ou reconexão). */
  onResyncNeeded?: (reason: string) => void;
}

/** Normaliza o status para o vocabulário cru, tolerando os nomes do enum. */
function mapStatus(status: string | undefined): ServerOrderStatus {
  switch (status) {
    case 'fila':
    case 'pendente':
      return 'fila';
    case 'preparando':
    case 'em_preparo':
      return 'preparando';
    case 'pronto':
      return 'pronto';
    case 'entregue':
      return 'entregue';
    case 'cancelado':
      return 'cancelado';
    default:
      wsLog.warn('[ws] status desconhecido, assumindo fila:', status);
      return 'fila';
  }
}

/** Normaliza a ação, tolerando maiúsculas e os nomes antigos. */
function normalizeAction(action: unknown): ServerAction | null {
  if (typeof action !== 'string') return null;
  switch (action.toLowerCase()) {
    case 'created':
    case 'create':
    case 'insert':
      return 'created';
    case 'update':
    case 'updated':
      return 'update';
    case 'deleted':
    case 'delete':
      return 'deleted';
    default:
      return null;
  }
}

class WebSocketOrderService {
  private ws: WebSocket | null = null;
  private config: WebSocketServiceConfig | null = null;
  private connectionStatus: WebSocketConnectionStatus = 'disconnected';

  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  /** Antes este timer era criado sem guardar a referência e nunca era limpo. */
  private connectionTimeout: ReturnType<typeof setTimeout> | null = null;
  private isManualDisconnect = false;
  /** Distingue a primeira conexão de uma reconexão (que exige recarga). */
  private hasConnectedBefore = false;

  private subscribers = new Set<(overlays: OrderStatusOverlay[]) => void>();
  private overlays = new Map<string, OrderStatusOverlay>();

  /**
   * Enquanto uma carga HTTP está em voo, os eventos ficam retidos e só são
   * aplicados depois — fechando a corrida entre o snapshot e o stream.
   */
  private snapshotInFlight = false;
  private bufferedEvents: Array<{ action: ServerAction; raw: Record<string, unknown> }> = [];

  connect(config: WebSocketServiceConfig): void {
    if (this.connectionStatus === 'connecting' || this.ws?.readyState === WebSocket.OPEN) {
      wsLog.debug('[ws] já conectado ou conectando, ignorando');
      return;
    }

    if (!WEBSOCKET_CONFIG.URL) {
      this.handleError(new Error('VITE_WS_URL não configurada'));
      return;
    }

    this.config = config;
    this.isManualDisconnect = false;
    this.setStatus('connecting');

    const url = new URL(WEBSOCKET_CONFIG.URL);
    url.searchParams.set('establishment_id', config.establishmentId);
    url.searchParams.set('payload', config.payload || 'raw');
    if (config.intervalId) {
      url.searchParams.set('interval_id', config.intervalId);
    }

    // O browser não permite header Authorization no handshake do WebSocket,
    // então o token vai na query. O servidor aceita conexão sem token durante
    // o período de tolerância, registrando em log quem ainda não envia.
    const token = apiClient.getStoredTokens()?.access_token || import.meta.env.VITE_TEST_TOKEN;
    if (token) {
      url.searchParams.set('token', token);
    }

    try {
      this.ws = new WebSocket(url.toString());
      this.setupEventHandlers();
      this.armConnectionTimeout();
    } catch (error) {
      this.handleError(new Error(`Falha ao abrir WebSocket: ${error}`));
    }
  }

  disconnect(): void {
    this.isManualDisconnect = true;
    this.clearTimers();

    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close(1000, 'Desconexão solicitada');
      this.ws = null;
    }

    this.setStatus('disconnected');
    this.config?.onDisconnect?.();
  }

  subscribe(callback: (overlays: OrderStatusOverlay[]) => void): () => void {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }

  getConnectionStatus(): WebSocketConnectionStatus {
    return this.connectionStatus;
  }

  getOverlays(): OrderStatusOverlay[] {
    return Array.from(this.overlays.values());
  }

  /**
   * Marca o início de uma carga HTTP. Devolve o instante a ser passado de volta
   * em commitSnapshot.
   */
  beginSnapshot(): number {
    this.snapshotInFlight = true;
    return Date.now();
  }

  /**
   * Conclui a carga HTTP: descarta os overlays recebidos ANTES de a busca
   * começar, porque a resposta já os incorpora, e aplica o que chegou durante.
   *
   * A comparação é entre dois instantes do relógio local — não há dependência
   * do horário do servidor nem risco de desvio de relógio.
   */
  commitSnapshot(startedAt: number): void {
    for (const [id, overlay] of this.overlays) {
      if (overlay.receivedAt < startedAt) {
        this.overlays.delete(id);
      }
    }

    this.snapshotInFlight = false;

    const buffered = this.bufferedEvents;
    this.bufferedEvents = [];
    for (const { action, raw } of buffered) {
      this.applyEvent(action, raw);
    }

    this.emit();
  }

  private setupEventHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      this.clearTimers();
      this.reconnectAttempts = 0;
      this.setStatus('connected');
      wsLog.debug('[ws] conectado', this.config?.establishmentId);

      // Toda reconexão exige recarga: eventos ocorridos enquanto o socket
      // estava fora não têm replay nem número de sequência, então seriam
      // perdidos em silêncio e a tela ficaria divergente do banco.
      if (this.hasConnectedBefore) {
        this.overlays.clear();
        this.config?.onResyncNeeded?.('reconexão');
      }
      this.hasConnectedBefore = true;

      this.config?.onConnect?.();
    };

    this.ws.onmessage = (event) => this.handleMessage(event);

    this.ws.onclose = (event) => {
      this.clearTimers();
      this.setStatus('disconnected');
      wsLog.debug('[ws] fechado', event.code, event.reason);

      if (!this.isManualDisconnect) {
        this.scheduleReconnect();
      } else {
        this.config?.onDisconnect?.();
      }
    };

    this.ws.onerror = () => {
      // onerror não traz detalhe útil no browser; o onclose seguinte é quem
      // conduz a reconexão
      wsLog.warn('[ws] erro de conexão');
    };
  }

  private handleMessage(event: MessageEvent): void {
    let data: Record<string, unknown>;
    try {
      data = JSON.parse(event.data);
    } catch (error) {
      wsLog.error('[ws] mensagem não é JSON válido', error);
      return;
    }

    const type = typeof data.type === 'string' ? data.type : undefined;

    // Mensagens de controle e de contadores não alteram os overlays.
    if (type === 'connected' || type === 'counters') {
      wsLog.debug('[ws] mensagem de controle:', type);
      return;
    }

    const action = normalizeAction(data.action);
    if (!action) {
      wsLog.warn('[ws] mensagem sem ação reconhecível:', type ?? '(sem type)');
      return;
    }

    // O envelope "full" aninha o pedido; o "raw" vem plano.
    const raw = (data.order && typeof data.order === 'object' ? data.order : data) as Record<string, unknown>;

    if (this.snapshotInFlight) {
      this.bufferedEvents.push({ action, raw });
      return;
    }

    if (this.applyEvent(action, raw)) {
      this.emit();
    }
  }

  /** Devolve true quando os overlays mudaram. */
  private applyEvent(action: ServerAction, raw: Record<string, unknown>): boolean {
    const orderId = typeof raw.order_id === 'string' ? raw.order_id : typeof raw.id === 'string' ? raw.id : null;
    if (!orderId) {
      wsLog.warn('[ws] evento sem order_id');
      return false;
    }

    // Pedido novo e remoção precisam dos dados completos (produtos, valor),
    // que o evento não carrega — o hook recarrega via HTTP, com debounce.
    if (action === 'created' || action === 'deleted') {
      this.config?.onResyncNeeded?.(action);
      return false;
    }

    const status = mapStatus(typeof raw.status === 'string' ? raw.status : undefined);
    const previous = this.overlays.get(orderId);
    if (previous?.status === status) {
      return false;
    }

    this.overlays.set(orderId, {
      id: orderId,
      status,
      order_number: raw.order_number != null ? String(raw.order_number) : previous?.order_number,
      student_name: typeof raw.student_name === 'string' ? raw.student_name : previous?.student_name,
      student_class: typeof raw.class_name === 'string' ? raw.class_name : previous?.student_class,
      receivedAt: Date.now(),
    });
    return true;
  }

  private emit(): void {
    const overlays = this.getOverlays();
    this.config?.onOverlays?.(overlays);
    this.subscribers.forEach((cb) => {
      try {
        cb(overlays);
      } catch (error) {
        wsLog.error('[ws] erro em assinante', error);
      }
    });
  }

  private setStatus(status: WebSocketConnectionStatus): void {
    if (this.connectionStatus === status) return;
    this.connectionStatus = status;
    this.config?.onStatusChange?.(status);
  }

  private handleError(error: Error): void {
    wsLog.error('[ws]', error.message);
    this.setStatus('error');
    this.config?.onError?.(error);
  }

  /**
   * Reconecta indefinidamente com backoff exponencial limitado.
   *
   * A versão anterior desistia após 3 tentativas e prometia "fallback to HTTP
   * polling" que não existia: uma oscilação de rede de poucos segundos
   * congelava o painel até alguém dar F5.
   */
  private scheduleReconnect(): void {
    if (this.isManualDisconnect || !this.config) return;

    const base = WEBSOCKET_CONFIG.RECONNECT_DELAY * WEBSOCKET_CONFIG.RECONNECT_BACKOFF_FACTOR ** this.reconnectAttempts;
    // jitter evita que todos os painéis reconectem no mesmo instante
    const delay = Math.min(base + Math.random() * 1000, WEBSOCKET_CONFIG.MAX_RECONNECT_DELAY);

    this.reconnectAttempts++;
    wsLog.debug(`[ws] reconectando em ${Math.round(delay)}ms (tentativa ${this.reconnectAttempts})`);

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      if (this.config) this.connect(this.config);
    }, delay);
  }

  private armConnectionTimeout(): void {
    this.connectionTimeout = setTimeout(() => {
      this.connectionTimeout = null;
      if (this.ws?.readyState === WebSocket.CONNECTING) {
        wsLog.warn('[ws] tempo esgotado no handshake');
        this.ws.close();
      }
    }, WEBSOCKET_CONFIG.CONNECTION_TIMEOUT);
  }

  private clearTimers(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
  }

  destroy(): void {
    this.disconnect();
    this.subscribers.clear();
    this.overlays.clear();
    this.bufferedEvents = [];
    this.hasConnectedBefore = false;
    this.config = null;
  }
}

// Não há heartbeat de aplicação, de propósito.
//
// O antigo enviava {type:"heartbeat"} a cada 30s e fechava a conexão se nada
// chegasse em 45s. O servidor nunca respondia a essa mensagem: ele usa PING do
// protocolo WebSocket, que o browser responde sozinho e o JavaScript nem
// enxerga. O watchdog só era satisfeito pelo próprio intervalo do cliente, ou
// seja, nunca detectava servidor morto — e em aba de segundo plano, onde o
// Chrome estrangula setInterval para 1x/min, ele disparava antes do próprio
// reset e derrubava a conexão sozinho.
//
// Detecção de conexão morta fica com o servidor (ping/pong com prazo de 60s) e
// com o onclose do browser, que é quem realmente sabe que o socket caiu.

let webSocketInstance: WebSocketOrderService | null = null;

export function getWebSocketOrderService(): WebSocketOrderService {
  if (!webSocketInstance) {
    webSocketInstance = new WebSocketOrderService();
  }
  return webSocketInstance;
}

export function createWebSocketOrderService(): WebSocketOrderService {
  return new WebSocketOrderService();
}

export function resetWebSocketOrderService(): void {
  webSocketInstance?.destroy();
  webSocketInstance = null;
}

export { WebSocketOrderService };
